/**
 * Drive API клиент — folder/file CRUD поверх /api/chat/drive/*
 */
import { api } from "@/api/client";

export interface DriveFolder {
  id: number;
  topic_id: number;
  parent_id: number | null;
  name: string;
  created_by_user_id: string;
  created_at: string;
  items_count: number;
}

export interface DriveFile {
  id: number;
  topic_id: number;
  folder_id: number | null;
  attachment_id: number;
  name: string;
  uploaded_by_user_id: string;
  uploaded_at: string;
  mime: string | null;
  size_bytes: number;
  width: number | null;
  height: number | null;
  duration_sec: number | null;
  thumb_b64: string | null;
}

export interface DriveListResp {
  folder_id: number | null;
  breadcrumbs: Array<{ id: number; name: string }>;
  folders: DriveFolder[];
  files: DriveFile[];
}

export const fetchDriveList = (topicId: number, parent: number | null = null): Promise<DriveListResp> => {
  const q = parent != null ? `?parent=${parent}` : "";
  return api(`/api/chat/drive/${topicId}/list${q}`);
};

export interface DriveSearchFolderHit {
  id: number;
  name: string;
  parent_id: number | null;
  path: string;
  items_count: number;
}
export interface DriveSearchFileHit {
  id: number;
  name: string;
  folder_id: number | null;
  folder_path: string;
  attachment_id: number;
  mime: string | null;
  size_bytes: number;
  width: number | null;
  height: number | null;
  duration_sec: number | null;
  thumb_b64: string | null;
  uploaded_at: string;
}
export interface DriveSearchResp {
  q: string;
  folders: DriveSearchFolderHit[];
  files: DriveSearchFileHit[];
}
export const searchDrive = (
  topicId: number,
  opts: { q?: string; uploaded_by?: string; date_from?: string }
): Promise<DriveSearchResp> => {
  const params = new URLSearchParams();
  if (opts.q) params.set("q", opts.q);
  if (opts.uploaded_by) params.set("uploaded_by", opts.uploaded_by);
  if (opts.date_from) params.set("date_from", opts.date_from);
  return api(`/api/chat/drive/${topicId}/search?${params}`);
};

export interface DriveUploader {
  id: string;
  display_name: string;
  avatar_filename: string | null;
  files_count: number;
}
export const fetchDriveUploaders = (topicId: number): Promise<DriveUploader[]> =>
  api(`/api/chat/drive/${topicId}/uploaders`);

export const createDriveFolder = (topicId: number, name: string, parentId: number | null = null): Promise<DriveFolder> =>
  api(`/api/chat/drive/${topicId}/folders`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, parent_id: parentId }),
  });

export const patchDriveFolder = (folderId: number, payload: { name?: string; parent_id?: number | null }): Promise<DriveFolder> =>
  api(`/api/chat/drive/folders/${folderId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

export const deleteDriveFolder = (folderId: number): Promise<void> =>
  api(`/api/chat/drive/folders/${folderId}`, { method: "DELETE" });

/** Перенести папку (с поддеревом) на другой Диск.
 *  target_parent_folder_id=null → положить в корень целевого диска. */
export const moveFolderToDrive = (
  folderId: number,
  targetTopicId: number,
  targetParentFolderId: number | null = null,
): Promise<DriveFolder> =>
  api(`/api/chat/drive/folders/${folderId}/move-to-drive`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      target_topic_id: targetTopicId,
      target_parent_folder_id: targetParentFolderId,
    }),
  });

export const uploadDriveFile = async (topicId: number, file: File, folderId: number | null = null): Promise<DriveFile> => {
  const fd = new FormData();
  fd.append("file", file);
  if (folderId != null) fd.append("folder_id", String(folderId));
  const base = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";
  const res = await fetch(`${base.replace(/\/api$/, "")}/api/chat/drive/${topicId}/files`, {
    method: "POST",
    credentials: "include",
    body: fd,
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(`Upload failed: ${res.status} ${txt}`);
  }
  return res.json();
};

export const patchDriveFile = (fileId: number, payload: { name?: string; folder_id?: number | null }): Promise<DriveFile> =>
  api(`/api/chat/drive/files/${fileId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

export const deleteDriveFile = (fileId: number): Promise<void> =>
  api(`/api/chat/drive/files/${fileId}`, { method: "DELETE" });

/** Корзина диска: soft-deleted файлы за последние 30 дней. */
export const fetchDriveTrash = (topicId: number): Promise<DriveFile[]> =>
  api(`/api/chat/drive/${topicId}/trash`);

/** Восстановить файл из корзины. */
export const restoreDriveFile = (fileId: number): Promise<DriveFile> =>
  api(`/api/chat/drive/files/${fileId}/restore`, { method: "POST" });

/** Закрепить документ (per-user). */
export const pinDoc = (attachmentId: number): Promise<{ pinned: boolean }> =>
  api(`/api/docs/${attachmentId}/pin`, { method: "POST" });

/** Открепить документ. */
export const unpinDoc = (attachmentId: number): Promise<{ pinned: boolean }> =>
  api(`/api/docs/${attachmentId}/pin`, { method: "DELETE" });

/** Закрепить документ ГЛОБАЛЬНО — видно как закреплённый у всех юзеров. */
export const pinDocForAll = (attachmentId: number): Promise<{ globally_pinned: boolean }> =>
  api(`/api/docs/${attachmentId}/pin-all`, { method: "POST" });

/** Снять глобальный pin. */
export const unpinDocForAll = (attachmentId: number): Promise<{ globally_pinned: boolean }> =>
  api(`/api/docs/${attachmentId}/pin-all`, { method: "DELETE" });

/** Удалить документ (отправить в корзину Диска всех drive_files,
 *  ссылающихся на этот attachment_id). */
export const deleteDoc = (attachmentId: number): Promise<{ deleted_drive_files: number }> =>
  api(`/api/docs/${attachmentId}`, { method: "DELETE" });

/** Создать копию документа. Если оригинал на Диске — копия будет на том же
 *  Диске, в той же папке. Возвращает {attachment_id, filename}. */
export const copyDoc = (attachmentId: number): Promise<{ attachment_id: number; filename: string }> =>
  api(`/api/docs/${attachmentId}/copy`, { method: "POST" });

/** Запреты на документ — действуют ВЕЗДЕ: в чате, на Диске, в редакторе. */
export interface DocRestrictions {
  download_blocked: boolean;
  copy_blocked: boolean;
  set_by_user_id?: string | null;
  set_at?: string | null;
}
export const fetchDocRestrictions = (attachmentId: number): Promise<DocRestrictions> =>
  api(`/api/docs/${attachmentId}/restrictions`);
export const setDocRestrictions = (attachmentId: number, payload: { download_blocked: boolean; copy_blocked: boolean }): Promise<DocRestrictions> =>
  api(`/api/docs/${attachmentId}/restrictions`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

/** Глобальная настройка: кто может удалять документы.
 *  'all' | 'moderators' | 'admins' | 'specific' (только перечисленные сотрудники) */
export type DeletePermission = "all" | "moderators" | "admins" | "specific";
export interface DeletePermissionResp {
  value: DeletePermission;
  user_ids: string[];
}
export const fetchDeletePermission = (): Promise<DeletePermissionResp> =>
  api(`/api/docs/settings/delete-permission`);
export const setDeletePermission = (
  value: DeletePermission,
  user_ids?: string[],
): Promise<DeletePermissionResp> =>
  api(`/api/docs/settings/delete-permission`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ value, user_ids: user_ids ?? null }),
  });

/** Прикрепить существующий chat_attachment к диску (без re-upload). */
export const attachToDrive = (topicId: number, payload: {
  attachment_id: number;
  folder_id?: number | null;
  name?: string;
}): Promise<DriveFile> =>
  api(`/api/chat/drive/${topicId}/files-from-attachment`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

/** Переименовать файл (только автор/superuser). Расширение сохраняется автоматически.
 *  После успеха — broadcast в кросс-табный канал, чтобы открытый OnlyOffice-редактор
 *  этого документа (в другой вкладке) сам перезагрузился с новым именем. */
export const renameAttachment = async (attachmentId: number, filename: string): Promise<{ attachment_id: number; filename: string }> => {
  const res = await api<{ attachment_id: number; filename: string }>(`/api/chat/attachments/${attachmentId}/rename`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ filename }),
  });
  try {
    const bc = new BroadcastChannel("kb-attachments");
    bc.postMessage({ type: "rename", attachment_id: res.attachment_id, new_filename: res.filename });
    bc.close();
  } catch { /* browsers без BroadcastChannel — ignore */ }
  return res;
};

// ─── Видео → Транскрипт / Постмит ───────────────────────────────────────
export interface VideoSaveTarget { drive_topic_id: number; drive_name: string; chat_topic_id: number; }
/** Диск отдела чата (куда по умолчанию сохранять транскрипт/постмит) — для пикера. */
export const getVideoSaveTarget = (attachmentId: number): Promise<VideoSaveTarget> =>
  api(`/api/chat/attachments/${attachmentId}/save-target`);
/** Запустить транскрибацию видео → docx в выбранную папку Диска (фоново). */
export const transcribeVideo = (attachmentId: number, driveTopicId: number, folderId: number | null): Promise<{ status: string }> =>
  api(`/api/chat/attachments/${attachmentId}/transcribe`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ drive_topic_id: driveTopicId, folder_id: folderId }),
  });
/** Запустить постмит по видео → docx в папку Диска + текст сообщением (фоново). */
export const makeVideoPostmit = (attachmentId: number, driveTopicId: number, folderId: number | null): Promise<{ status: string }> =>
  api(`/api/chat/attachments/${attachmentId}/postmit`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ drive_topic_id: driveTopicId, folder_id: folderId }),
  });
